@if (hasValidAmount()) {
  <section class="flex flex-col gap-2 pt-3 border-t border-surface-200" aria-label="Incentive distribution preview">
    @if (isLoading()) {
      <div class="flex items-center justify-between">
        <p-skeleton width="14rem" height="0.875rem" />
      </div>
      <div class="flex flex-col gap-1.5">
        @for (i of [1, 2, 3]; track i) {
          <div class="flex items-center justify-between gap-3 py-1.5">
            <p-skeleton width="10rem" height="1rem" />
            <div class="flex items-center gap-4">
              <p-skeleton width="4rem" height="1rem" />
              <p-skeleton width="4rem" height="1rem" />
              <p-skeleton width="2.5rem" height="1rem" />
            </div>
          </div>
        }
      </div>
      <div class="flex items-center justify-between pt-1">
        <p-skeleton width="16rem" height="0.875rem" />
        <p-skeleton width="8rem" height="0.875rem" />
      </div>
    } @else if (hasError()) {
      <div class="flex items-center gap-2 text-body-xs-400 text-surface-500" role="status">
        <i class="pi pi-info-circle text-body-xs"></i>
        <span>Preview unavailable.</span>
        <button
          type="button"
          class="text-body-xs-500 text-surface-600 hover:text-surface-800 underline bg-transparent border-none p-0 cursor-pointer"
          (click)="retry()"
        >
          Retry
        </button>
      </div>
    } @else if (preview()) {
      <div class="text-body-xs-500 text-surface-500 uppercase tracking-wide">
        Preview · How {{ preview()!.requestedAmount | currencyFormat }} will land
      </div>

      @if (isEmpty()) {
        <div
          class="flex items-center gap-2 py-2 px-3 rounded-md bg-surface-50 text-body-sm-500 text-surface-600"
          role="status"
        >
          <i class="pi pi-info-circle text-surface-500 text-body-sm"></i>
          <span>No options in this addendum to receive the incentive — the full amount returns to the pool.</span>
        </div>
      } @else {
        <ul class="flex flex-col m-0 p-3 list-none border border-surface-100 rounded-md overflow-hidden">
          @for (row of rows(); track row.id) {
            <li
              class="flex items-center justify-between gap-3 py-1.5 border-0 border-b border-surface-100 last:border-b-0"
              [class.opacity-60]="row.skipped"
            >
              <span
                class="text-body-sm-500 truncate"
                [class.text-surface-700]="!row.skipped"
                [class.text-surface-400]="row.skipped"
              >
                {{ row.label }}
              </span>
              <div class="flex items-center gap-4 shrink-0">
                <span class="text-body-sm-400 text-surface-500 w-20 text-right">
                  {{ row.cost | currencyFormat }}
                </span>
                <span
                  class="text-body-sm-600 w-20 text-right"
                  [class.text-emerald-700]="!row.skipped"
                  [class.text-surface-400]="row.skipped"
                >
                  @if (row.skipped) {
                    —
                  } @else {
                    {{ row.allocated | currencyFormat }}
                  }
                </span>
                <span
                  class="text-body-xs-500 w-16 text-right"
                  [class.text-surface-500]="!row.skipped"
                  [class.text-surface-400]="row.skipped"
                >
                  @if (row.skipped) {
                    skipped
                  } @else {
                    {{ row.percentage }}%
                  }
                </span>
              </div>
            </li>
          }
        </ul>
      }

      <div class="flex items-center justify-between flex-wrap gap-2 pt-1">
        <div class="text-body-xs-500 text-surface-600">
          Consumed:
          <span class="text-emerald-700 text-body-xs-600">
            {{ preview()!.allocatedAmount | currencyFormat }}
          </span>
          <span class="text-surface-400 px-1">·</span>
          Remaining (back to pool):
          <span class="text-surface-700 text-body-xs-600">
            {{ preview()!.remainderToPool | currencyFormat }}
          </span>
        </div>
        @if (!isEmpty()) {
          <div class="text-body-xs-400 text-surface-500 inline-flex items-center gap-1">
            Σ allocations = consumed
            @if (allocationsMatch()) {
              <i class="pi pi-check text-emerald-600 text-body-xs"></i>
            } @else {
              <i class="pi pi-times text-amber-600 text-body-xs"></i>
            }
          </div>
        }
      </div>
    }
  </section>
}
